import React, { useState } from 'react';
import { useTradingContext } from '../../context/TradingContext';
import { Search, Star, TrendingUp, TrendingDown, Clock } from 'lucide-react';

export default function MarketList() {
  const { spotPairs, futuresPairs, tradingMode, selectedPair, setSelectedPair, marketPrices } = useTradingContext();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); 
  
  const pairs = tradingMode === 'spot' ? spotPairs : futuresPairs;
  
  const filteredPairs = pairs.filter(p => {
      const s = search.toLowerCase();
      const symbol = (p.symbol || "").toLowerCase();
      const base = (p.baseAsset || p.base_symbol || p.base_asset || "").toLowerCase();
      
      const matchSearch = symbol.includes(s) || base.includes(s);
      
      if (filter === 'favorites') return matchSearch && p.isFavorite;
      return matchSearch;
  });

  const displayPairs = search ? filteredPairs : filteredPairs.slice(0, 50);

  return (
    <div className="trading-card flex flex-col h-full overflow-hidden">
      <div className="p-3 border-b border-white/5 space-y-3">
        <div className="flex gap-4 border-b border-white/5">
            <button 
                className={`text-[9px] font-black uppercase tracking-widest pb-2 transition-all border-b-2 ${filter === 'all' ? 'text-white border-indigo-500' : 'text-slate-500 border-transparent hover:text-slate-300'}`} 
                onClick={() => setFilter('all')}
            >
                All
            </button>
            <button 
                className={`text-[9px] font-black uppercase tracking-widest pb-2 transition-all border-b-2 ${filter === 'favorites' ? 'text-white border-indigo-500' : 'text-slate-500 border-transparent hover:text-slate-300'}`} 
                onClick={() => setFilter('favorites')}
            >
                Favorites
            </button>
        </div>
        
        <div className="relative">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <input 
            type="text" 
            placeholder="Search market..." 
            className="w-full bg-slate-950/50 border border-white/5 rounded-lg py-1.5 pl-9 pr-3 text-[10px] font-bold text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/30 transition-all"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      
      <div className="px-4 py-1.5 grid grid-cols-12 text-[8px] font-black text-slate-600 uppercase tracking-widest border-b border-white/5 bg-slate-950/20">
        <span className="col-span-6">Market</span>
        <span className="col-span-3 text-right">Price</span>
        <span className="col-span-3 text-right">24h</span>
      </div>
      
      <div className="flex-1 overflow-y-auto custom-scrollbar p-1 space-y-0.5">
        {displayPairs.map(pair => {
            const isSelected = selectedPair?.id === pair.id || (pair.symbol && selectedPair?.symbol === pair.symbol);
            const marketData = marketPrices[pair.symbol];
            
            let displayPrice = "---";
            let displayChange = "0.00%";
            let isPositive = true;

            if (marketData && marketData.price) {
                displayPrice = marketData.price >= 1 
                    ? marketData.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
                    : marketData.price.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 });
                displayChange = (marketData.changePercent >= 0 ? '+' : '') + (marketData.changePercent || 0).toFixed(2) + '%';
                isPositive = marketData.changePercent >= 0;
            }

            const baseName = pair.baseAsset || pair.base_symbol || pair.base_asset || (pair.symbol ? pair.symbol.replace('USDT', '') : '???');

            return (
                <div 
                    key={pair.id || pair.symbol}
                    onClick={() => setSelectedPair(pair)}
                    className={`grid grid-cols-12 py-2 px-3 rounded-lg cursor-pointer transition-all items-center group relative ${isSelected ? 'bg-indigo-500/10 border border-indigo-500/20' : 'border border-transparent hover:bg-white/5'}`}
                >
                    <div className="col-span-6 flex flex-col min-w-0">
                        <div className="flex items-center gap-1.5">
                            <span className="text-[11px] font-black text-white truncate font-outfit uppercase tracking-tight">{baseName}</span>
                            <span className="text-[8px] font-black text-slate-600 uppercase">/USDT</span>
                        </div>
                        <span className="text-[8px] font-bold text-slate-600 uppercase truncate">Vol: {marketData?.volume ? (marketData.volume / 1000000).toFixed(2) + 'M' : "---"}</span>
                    </div>
                    
                    <div className="col-span-3 text-right">
                        <div className={`text-[10px] font-black font-outfit tracking-tighter truncate ${isSelected ? 'text-white' : 'text-slate-200'}`}>{displayPrice}</div>
                    </div>
                    
                    <div className="col-span-3 flex flex-col items-end">
                        <div className={`text-[9px] font-black font-outfit ${isPositive ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {displayChange}
                        </div>
                    </div>
                </div>
            );
        })}
        
        {filteredPairs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-slate-700 space-y-2">
                <Search size={20} strokeWidth={1.5} />
                <p className="text-[9px] font-black uppercase tracking-widest">No markets found</p>
            </div>
        )}
      </div>
    </div>
  );
}
